import MCCache from '../lib/cache'
import { workflows } from '../lib/api'
import {
    MetaEntry,
    Workflow,
    WorkflowAssignedUsers,
    WorkflowConfig,
    WorkflowStep,
} from '../lib/types'
import * as jsyaml from 'js-yaml'
import auth from '../lib/auth'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { useLocation } from 'react-router-dom'
import getId from '../lib/get-id'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(customParseFormat)

const cycleSteps = ['correct-cycle', 'input-cycle']

export async function getWorkflow(id: number) {
    let key = 'workflow:' + id
    let wf: Workflow = MCCache.get(key)
    if (!wf) {
        wf = await workflows.get(id)
        MCCache.set(key, wf)
    }
    return wf
}

export async function getWorkflowConfig(id: number) {
    let key = 'workflow-config:' + id
    let config: WorkflowConfig = MCCache.get(key)
    if (!config) {
        let wf = await getWorkflow(id)
        config = jsyaml.load(wf.config) as WorkflowConfig
        config.users = wf.users
        MCCache.set(key, config)
    }
    return config
}

export function hasWorkflowStepAccess(
    config: WorkflowConfig,
    stepIndex: number,
    userRoles: WorkflowAssignedUsers[],
): boolean {
    let isAdmin = auth.checkRole('admin', 'workflowAdmin', 'lead')
    let roles = userRoles.map((item) => item.role)
    let step = config.steps[stepIndex]
    let hasStepRole = !!step.roles.find((r) => roles.includes(r.toLowerCase()))
    return isAdmin || hasStepRole
}

export function hasWorkflowAccess(user: WorkflowAssignedUsers): boolean {
    const userSub = auth.user?.sub
    if (!userSub) return false
    let hasRole = user.id === Number(auth.user?.sub)
    return hasRole
}

export function findCycleStep(config: WorkflowConfig): boolean {
    for (let x = 0; x < config.steps.length; x++) {
        if (isCycleStep(config.steps[x])) {
            return true
        }
    }
    return false
}

export function isCycleStep(step: WorkflowStep) {
    return cycleSteps.includes(step.type)
}

export function getCurrentPage() {
    let pagenum: number = MCCache.get('page')
    if (!pagenum) {
        return 0
    }
    return pagenum
}

export function setCurrentPage(setPage: (page: number) => void, page: number) {
    MCCache.set('page', page)
    setPage(page)
}

export const workflowStatusOptions = [
    { id: 0, label: 'All', value: '', backgroundColor: '' },
    { id: 1, label: 'Active', value: 'active', backgroundColor: '#56CA00' },
    { id: 2, label: 'Inactive', value: 'inactive', backgroundColor: '#1A1A1A12' },
    { id: 3, label: 'Archive', value: 'archive', backgroundColor: '#B0C4DE' },
    {
        id: 4,
        label: 'Inprogress',
        value: 'inprogress',
        backgroundColor: '#FFEF9D',
    },
]

export const userStatusOptions = [
    { id: 0, label: 'All', value: '' },
    { id: 1, label: 'Active', value: 1 },
    { id: 2, label: 'Inactive', value: 0 },
]

export const formatDate = (date: string) => {
    const formats = [
        'YYYY-MM-DD',
        'MM/DD/YYYY',
        'YYYY-MM-DDTHH:mm:ss',
        'YYYY-MM-DDTHH:mm:ss.SSS',
        'YYYY-MM-DDTHH:mm:ss.SSSZ',
        'YYYY-MM-DDTHH:mm:ssZ',
    ]
    const isValid = formats.some((format) => dayjs(date, format, false).isValid())
    if (isValid) {
        return dayjs(new Date(date)).format('YYYY-MM-DD')
    }
    return undefined
}

export const compareByColumnAsc = (column: any) => {
    return function (a: any, b: any) {
        if (a[column] > b[column]) {
            return 1 // Swap a and b
        }
        if (a[column] < b[column]) {
            return -1 // Keep a and b in the same order
        }
        return 0 // Keep a and b in the same order (equal values)
    }
}

export const useQuery = () => {
    return new URLSearchParams(useLocation().search)
}

export function findRowMeta(
    meta: Array<MetaEntry>,
    row: any,
    isArbitrate?: boolean,
): MetaEntry | undefined {
    if (isArbitrate) {
        return meta.find((m) => Number(m.srcId) === row.id)
    } else {
        return meta.find((m) => Number(m.objId) === getId(row))
    }
}
